import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ExternalLink, Plus, Trash2 } from 'lucide-react';
import {
  getCourseAdmin,
  updateCourseAdmin,
  createModuleAdmin,
  deleteModuleAdmin,
  createLessonAdmin,
  updateLessonAdmin,
  deleteLessonAdmin,
} from '../../lib/admin';
import AssessEditor from './AssessEditor';
import type { LmsCourse } from '../../types/lms';
import { EmptyState, Panel, PanelHead, Stamp, fieldCls, labelCls } from '../../components/lms/ui';
import { CourseEditorSkeleton } from '../../components/lms/Skeletons';
import { marketingHref } from '../../lib/surface';

const LessonChevron = () => (
  <ChevronDown
    className="h-4 w-4 flex-none text-ink-2 transition-transform duration-200 group-open:rotate-180"
    strokeWidth={1.5}
    aria-hidden="true"
  />
);

const CourseEditor: React.FC = () => {
  const { id = '' } = useParams();
  const [course, setCourse] = useState<LmsCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ title: '', description: '', tagline: '', price_value: 0, duration: '', level: 'Beginner', category: 'Certificate', thumbnail: '', is_featured: true });
  const [newModule, setNewModule] = useState('');
  const [newLesson, setNewLesson] = useState<Record<string, { title: string; video_url: string; duration: string; resources: string; content: string }>>({});

  const load = async () => {
    setLoading(true);
    try {
      const c = await getCourseAdmin(id);
      setCourse(c);
      if (c) setForm({ title: c.title, description: c.description, tagline: c.tagline, price_value: c.price_value, duration: c.duration, level: c.level, category: c.category, thumbnail: c.thumbnail || '', is_featured: c.is_featured !== false });
    } finally {
      setLoading(false);
    }
  };

  // Reload when the route id changes; load is intentionally scoped to that id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [id]);

  const saveDetails = async () => {
    setSaving(true);
    setError('');
    try {
      await updateCourseAdmin(id, { ...form, thumbnail: form.thumbnail || null } as never);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the programme details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CourseEditorSkeleton />;
  }

  if (!course) {
    return (
      <Panel>
        <EmptyState
          title="Programme not on file"
          body="This programme may have been deleted or the link is out of date. Return to the operations desk to choose another record."
          action={<Link to="/admin" className="lms-btn-secondary"><ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Operations desk</Link>}
        />
      </Panel>
    );
  }

  return (
    <div>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-2 hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Operations desk
      </Link>

      <header className="mt-4 flex flex-wrap items-end justify-between gap-x-8 gap-y-4 border-b border-rule pb-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <Stamp tone={course.is_published ? 'cleared' : 'neutral'}>{course.is_published ? 'Published' : 'Draft'}</Stamp>
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">{course.id}</span>
          </div>
          <h1 className="mt-2 max-w-[40ch] text-[clamp(1.5rem,2.4vw,2rem)] font-semibold leading-tight tracking-[-0.025em] text-ink">
            {course.title}
          </h1>
          <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-2">
            {course.modules.length} modules · {course.modules.reduce((n, m) => n + m.lessons.length, 0)} lessons · ₦{course.price_value.toLocaleString('en-NG')}
          </p>
        </div>
        <a href={marketingHref(`/course/${course.id}`)} target="_blank" rel="noreferrer" className="lms-btn-secondary">
          View public page <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
        </a>
      </header>

      {error && (
        <p className="mt-4 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
      )}

      <div className="mt-6 space-y-6">
        <Panel>
          <PanelHead
            title="Programme details"
            action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Student-facing record</span>}
          />
          <form className="space-y-4 px-5 py-4" onSubmit={(e) => { e.preventDefault(); void saveDetails(); }}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="edit-title" className={labelCls}>Title</label>
                <input id="edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={fieldCls} />
              </div>
              <div>
                <label htmlFor="edit-tagline" className={labelCls}>Tagline</label>
                <input id="edit-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={fieldCls} />
              </div>
            </div>

            <div>
              <label htmlFor="edit-description" className={labelCls}>Description / overview</label>
              <textarea id="edit-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={fieldCls} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label htmlFor="edit-price" className={labelCls}>Price (NGN)</label>
                <input id="edit-price" type="number" value={form.price_value} onChange={(e) => setForm({ ...form, price_value: Number(e.target.value) })} className={`${fieldCls} tnum`} />
              </div>
              <div>
                <label htmlFor="edit-duration" className={labelCls}>Duration</label>
                <input id="edit-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={fieldCls} />
              </div>
              <div>
                <label htmlFor="edit-level" className={labelCls}>Level</label>
                <input id="edit-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className={fieldCls} />
              </div>
              <div>
                <label htmlFor="edit-category" className={labelCls}>Category</label>
                <input id="edit-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={fieldCls} />
              </div>
            </div>

            <div>
              <label htmlFor="edit-thumbnail" className={labelCls}>Thumbnail image URL</label>
              <input id="edit-thumbnail" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} placeholder="https://…" className={fieldCls} />
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-rule bg-paper/50 px-4 py-3">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-rule text-blue-600 focus:ring-blue-500"
              />
              <span>
                <span className="block text-[12.5px] font-semibold text-ink">Feature on the public homepage</span>
                <span className="mt-0.5 block text-[11.5px] leading-5 text-ink-2">
                  Published programmes are eligible for the homepage featured section. Hide this one without unpublishing it.
                </span>
              </span>
            </label>

            <div className="flex items-center justify-between gap-3 border-t border-rule pt-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Changes save to the programme record</p>
              <button type="submit" disabled={saving} className="lms-btn-primary !px-4 !py-2 !text-[12px]">
                {saving ? 'Saving…' : 'Save details'}
              </button>
            </div>
          </form>
        </Panel>

        <Panel>
          <PanelHead
            title="Modules & lessons"
            action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{course.modules.length} modules</span>}
          />

          <form
            className="flex flex-col gap-2 border-b border-rule px-5 py-4 sm:flex-row sm:items-end"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!newModule.trim()) return;
              setError('');
              try {
                await createModuleAdmin(id, newModule.trim());
                setNewModule('');
                await load();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not add the module.');
              }
            }}
          >
            <div className="min-w-0 flex-1">
              <label htmlFor="new-module" className={labelCls}>New module</label>
              <input id="new-module" value={newModule} onChange={(e) => setNewModule(e.target.value)} placeholder="Module title" className={fieldCls} />
            </div>
            <button type="submit" className="lms-btn-secondary !px-3.5 !py-2 !text-[12px]">
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add module
            </button>
          </form>

          {course.modules.length === 0 ? (
            <EmptyState title="No modules yet" body="Add the first module above, then author its lessons. Students see modules in the order they appear here." />
          ) : (
            <div className="divide-y divide-rule">
              {course.modules.map((m, mi) => (
                <section key={m.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-paper/55 px-5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="font-mono text-[10px] font-semibold text-ink-2">{String(mi + 1).padStart(2, '0')}</span>
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-semibold text-ink">{m.title}</p>
                        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{m.lessons.length} {m.lessons.length === 1 ? 'lesson' : 'lessons'}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm(`Delete module ${m.title}?`)) return;
                        await deleteModuleAdmin(m.id);
                        await load();
                      }}
                      className="lms-btn-quiet !px-2 !py-1.5 !text-[11px] hover:!text-danger"
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Delete module
                    </button>
                  </div>

                  <div className="divide-y divide-rule/70">
                    {m.lessons.map((l, li) => (
                      <details key={l.id} className="group">
                        <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-3 hover:bg-paper/60 [&::-webkit-details-marker]:hidden">
                          <span className="font-mono text-[9px] text-ink-2">{String(li + 1).padStart(2, '0')}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[12.5px] font-medium text-ink">{l.title}</span>
                            <span className="mt-0.5 block font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2">
                              {l.video_url ? 'Video attached' : 'No video'} · {l.is_free ? 'Free preview' : 'Enrolled only'}
                            </span>
                          </span>
                          <span className="hidden font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2 sm:block">Edit lesson</span>
                          <LessonChevron />
                        </summary>

                        <div className="space-y-2.5 border-t border-rule/70 bg-paper/40 px-5 py-4">
                          <div className="grid gap-2.5 sm:grid-cols-2">
                            <div>
                              <label htmlFor={`t-${l.id}`} className={labelCls}>Lesson title</label>
                              <input defaultValue={l.title} id={`t-${l.id}`} className={fieldCls} />
                            </div>
                            <div>
                              <label htmlFor={`v-${l.id}`} className={labelCls}>Video URL</label>
                              <input
                                defaultValue={l.video_url || ''}
                                id={`v-${l.id}`}
                                placeholder="YouTube or mp4 URL"
                                className={fieldCls}
                              />
                            </div>
                          </div>
                          <div>
                            <label htmlFor={`c-${l.id}`} className={labelCls}>Lesson notes / description</label>
                            <input defaultValue={l.content} id={`c-${l.id}`} className={fieldCls} />
                          </div>
                          <div>
                            <label htmlFor={`r-${l.id}`} className={labelCls}>Resources — one per line, format: Label | URL</label>
                            <textarea
                              defaultValue={l.resources}
                              id={`r-${l.id}`}
                              rows={2}
                              placeholder={'Workbook | https://…/file.pdf'}
                              className={fieldCls}
                            />
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-3">
                            <button
                              type="button"
                              onClick={async () => {
                                if (!confirm(`Delete lesson ${l.title}?`)) return;
                                await deleteLessonAdmin(l.id);
                                await load();
                              }}
                              className="lms-btn-quiet !px-2.5 !py-1.5 !text-[11px] hover:!text-danger"
                            >
                              Delete lesson
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const t = (document.getElementById(`t-${l.id}`) as HTMLInputElement)?.value || l.title;
                                const v = (document.getElementById(`v-${l.id}`) as HTMLInputElement)?.value || null;
                                const c = (document.getElementById(`c-${l.id}`) as HTMLInputElement)?.value || '';
                                const r = (document.getElementById(`r-${l.id}`) as HTMLTextAreaElement)?.value || '';
                                setError('');
                                try {
                                  await updateLessonAdmin(l.id, { title: t, video_url: v, content: c, resources: r });
                                  await load();
                                } catch (err) {
                                  setError(err instanceof Error ? err.message : 'Could not save the lesson.');
                                }
                              }}
                              className="lms-btn-primary !px-3.5 !py-1.5 !text-[11px]"
                            >
                              Save lesson
                            </button>
                          </div>
                        </div>
                      </details>
                    ))}
                    {m.lessons.length === 0 && (
                      <p className="px-5 py-3 text-[12px] text-ink-2">No lessons in this module yet.</p>
                    )}
                  </div>

                  <details className="group border-t border-rule/70">
                    <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-2.5 text-[11.5px] font-medium text-pine hover:bg-paper/60 [&::-webkit-details-marker]:hidden">
                      <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add a lesson
                    </summary>
                    <form
                      className="space-y-2.5 border-t border-rule/70 bg-paper/40 px-5 py-4"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const nl = newLesson[m.id];
                        if (!nl?.title.trim()) return;
                        setError('');
                        try {
                          await createLessonAdmin(m.id, {
                            title: nl.title.trim(),
                            video_url: nl.video_url || null,
                            content: nl.content || '',
                            resources: nl.resources || '',
                          });
                          setNewLesson({ ...newLesson, [m.id]: { title: '', video_url: '', duration: '', content: '', resources: '' } });
                          await load();
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Could not add the lesson.');
                        }
                      }}
                    >
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        <div>
                          <label htmlFor={`nl-title-${m.id}`} className={labelCls}>Lesson title</label>
                          <input
                            id={`nl-title-${m.id}`}
                            value={newLesson[m.id]?.title || ''}
                            onChange={(e) => setNewLesson({ ...newLesson, [m.id]: { title: e.target.value, video_url: newLesson[m.id]?.video_url || '', duration: '', content: newLesson[m.id]?.content || '', resources: newLesson[m.id]?.resources || '' } })}
                            placeholder="e.g. Reading a bill of lading"
                            className={fieldCls}
                          />
                        </div>
                        <div>
                          <label htmlFor={`nl-video-${m.id}`} className={labelCls}>Video URL (optional)</label>
                          <input
                            id={`nl-video-${m.id}`}
                            value={newLesson[m.id]?.video_url || ''}
                            onChange={(e) => setNewLesson({ ...newLesson, [m.id]: { title: newLesson[m.id]?.title || '', video_url: e.target.value, duration: '', content: newLesson[m.id]?.content || '', resources: newLesson[m.id]?.resources || '' } })}
                            className={fieldCls}
                          />
                        </div>
                      </div>
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        <div>
                          <label htmlFor={`nl-content-${m.id}`} className={labelCls}>Lesson notes / description</label>
                          <input
                            id={`nl-content-${m.id}`}
                            value={newLesson[m.id]?.content || ''}
                            onChange={(e) => setNewLesson({ ...newLesson, [m.id]: { title: newLesson[m.id]?.title || '', video_url: newLesson[m.id]?.video_url || '', duration: '', content: e.target.value, resources: newLesson[m.id]?.resources || '' } })}
                            className={fieldCls}
                          />
                        </div>
                        <div>
                          <label htmlFor={`nl-resources-${m.id}`} className={labelCls}>Resources</label>
                          <textarea
                            id={`nl-resources-${m.id}`}
                            value={newLesson[m.id]?.resources || ''}
                            onChange={(e) => setNewLesson({ ...newLesson, [m.id]: { title: newLesson[m.id]?.title || '', video_url: newLesson[m.id]?.video_url || '', duration: '', content: newLesson[m.id]?.content || '', resources: e.target.value } })}
                            rows={2}
                            placeholder={'Workbook | https://…/file.pdf'}
                            className={fieldCls}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end border-t border-rule pt-3">
                        <button type="submit" className="lms-btn-secondary !px-3.5 !py-1.5 !text-[11px]">
                          Add lesson
                        </button>
                      </div>
                    </form>
                  </details>
                </section>
              ))}
            </div>
          )}
        </Panel>

        <AssessEditor courseId={id} />
      </div>
    </div>
  );
};

export default CourseEditor;
